// src/components/forms/shared/BookingCardInvoiceHistory.tsx

import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  CheckCircleOutline as SuccessIcon,
  ErrorOutline as FailIcon,
} from '@mui/icons-material';

interface EmailLogEntry {
  id: number;
  sentAt: string;
  sentByUsername: string;
  success: boolean;
  errorMessage?: string;
}

interface BookingCardInvoiceHistoryProps {
  emailLog: EmailLogEntry[];
  label?: string;
  onSendNow?: () => void;
  sendDisabled?: boolean;
}

const formatDateTime = (isoString: string): string =>
  new Date(isoString).toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const BookingCardInvoiceHistory: React.FC<BookingCardInvoiceHistoryProps> = ({
  emailLog,
  label = 'Invoice',
  onSendNow,
  sendDisabled,
}) => {
  const theme = useTheme();

  const rowBg = (idx: number) =>
    idx % 2 === 0
      ? 'transparent'
      : theme.palette.mode === 'dark'
        ? 'rgba(33, 150, 243, 0.08)'
        : 'rgba(0, 0, 0, 0.1)';

  const borderBottom = `2px dashed ${theme.palette.dividerContrast}`;

  return (
    <Box sx={{ width: '100%', maxWidth: '800px' }}>
      <Box>
        {emailLog.length > 0 ? (
          emailLog.map((entry, idx) => (
            <Box
              key={entry.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pr: '1rem',
                pl: '1rem',
                py: '0.5rem',
                minHeight: '36px',
                borderBottom,
                backgroundColor: rowBg(idx),
                overflowX: 'auto',
                touchAction: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                whiteSpace: 'nowrap',
                '@media (max-width: 600px)': { pr: 1.5, pl: 1.5 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {entry.success ? (
                  <SuccessIcon
                    sx={{ fontSize: '2.5rem', color: 'success.main' }}
                  />
                ) : (
                  <FailIcon
                    sx={{ fontSize: '2.5rem', color: 'error.main' }}
                    titleAccess={entry.errorMessage ?? 'Send failed'}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: '1.8rem',
                    fontWeight: '400',
                    flexShrink: 0,
                    ml: 0.75,
                  }}
                >
                  {formatDateTime(entry.sentAt)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{ fontSize: '1.6rem', color: 'text.secondary', ml: 1 }}
                >
                  by {entry.sentByUsername}
                </Typography>
              </Box>
            </Box>
          ))
        ) : (
          <Box
            sx={{
              pr: '1rem',
              pl: '1rem',
              py: '0.5rem',
              minHeight: '36px',
              borderBottom,
              display: 'flex',
              alignItems: 'center',
              '@media (max-width: 600px)': { pr: 1.5, pl: 1.5 },
            }}
          >
            <Typography sx={{ fontSize: '1.8rem', fontWeight: '400' }}>
              Unsent
              {onSendNow && !sendDisabled && (
                <>
                  <Typography
                    component="span"
                    sx={{ fontSize: '1.8rem', ml: '0.5rem' }}
                  >
                    -
                  </Typography>
                  <Typography
                    component="span"
                    onClick={onSendNow}
                    sx={{
                      fontSize: '1.8rem',
                      color: 'primary.main',
                      cursor: 'pointer',
                      ml: '0.5rem',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: 'secondary.main',
                      },
                    }}
                  >
                    send now
                  </Typography>
                </>
              )}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BookingCardInvoiceHistory;
